import psiPlatformAdapter from '../../psiPlatformAdapter';

export interface InactivityRateData {
  deregistration_rate: number;
  total_processes: number;
  mes: string;
  year: number;
  ranking: string;
  internal_limit: number;
  internal_limit_requested: number;
  processes_first_session_30d: number;
  second_session_30d: number;
  fifth_session_pct: number;
  bloqueos: string;
  nombre: string;
}

/**
 * Tasa de inactividad de la psicóloga autenticada.
 *
 * Antes esto le pegaba a un webhook de Make con la URL hardcodeada acá y el
 * `psychologist_id` por query. La URL quedaba en el bundle público y el webhook
 * no valida nada: con cambiar el id se leían las métricas de cualquier
 * psicóloga. Ahora lo sirve psiplatform-be con el id sacado del JWT.
 *
 * Ya no toma `psychologistId` — el backend no lo acepta, y volver a pasarlo
 * reabriría ese agujero.
 *
 * @param month `YYYY-MM`
 */
export async function fetchInactivityRate(
  month: string,
): Promise<InactivityRateData> {
  return psiPlatformAdapter.metricsInactivityRate(month);
}

// ─── Detail webhook (3.2) ─────────────────────────────────────────────────────

export interface InactivityDetailProcess {
  process: string;                          // Airtable record ID del proceso
  patient_name: string | null;
  rol_en_calculo: string;                   // 'numerador' | 'denominador' | 'excluido_*' | 'alta_en_mes'
  total_sesiones: number;
  activation_date: string;                  // ISO — inicio del proceso
  ultima_sesion_date_absoluta: string | null; // ISO — última sesión
  patient_status_actual: string | null;     // 'active' | 'inactive' | null
  patient_status_al_fin: string | null;
  es_reactivado: 0 | 1;
  es_baja_prematura: boolean | null;
  fecha_baja: string | null;
  deregistration_date: string | null;
  gap_dias: number | null;
  es_transferred_en_mes: 0 | 1;
  segmento_actual: string | null;
  transito_a_dereg_en_mes: boolean | null;
}

export interface InactivityDetailData {
  mes: string;                               // "YYYY-MM"
  psico: string;
  denominador: number;
  numerador: number;
  excluidos_por_sesiones: number;
  procesos_vigentes: number;
  procesos_vigentes_reactivados: number;
  procesos_vigentes_transferred: number;
  procesos_alta_en_mes: number;
  procesos_alta_en_mes_vigentes: number;
  procesos_alta_en_mes_baja: number;
  deregistration_rate: string;              // "x.xx%"
  procesos: InactivityDetailProcess[];
}

// Para psicólogas en pending_discharged, fetchInactivityRate no las encuentra (fuera del roster
// activo que evalúa ese webhook), pero fetchInactivityDetail sí las procesa igual. numerador/
// denominador vienen del mismo cálculo que expone InactivityRateData.deregistration_rate, así que
// son la fuente confiable de reemplazo para ese caso puntual.
export function rateFromDetail(detail: InactivityDetailData): number {
  return detail.denominador > 0 ? detail.numerador / detail.denominador : 0;
}

/**
 * Detalle de inactividad por proceso de la psicóloga autenticada.
 *
 * ⚠️ Esta respuesta trae `patient_name` y el estado de cada proceso: son datos
 * de pacientes. Hasta este cambio venían de un webhook de Make SIN
 * AUTENTICACIÓN cuya URL estaba en el bundle de producción, y que aceptaba
 * cualquier `psychologist_id` por body — o sea que se podían leer los pacientes
 * de cualquier psicóloga. Ahora lo sirve psiplatform-be detrás de PsiAuthGuard
 * y el id sale del JWT.
 *
 * Ya no toma `psychologistId`. No se lo vuelvas a agregar.
 *
 * @param month `MM`
 * @param year  `YYYY`
 */
export async function fetchInactivityDetail(
  month: string,
  year: string,
): Promise<InactivityDetailData> {
  return psiPlatformAdapter.metricsInactivityDetail(month, year);
}
